import {IpmErrorBoundary, reportIpmVisible, type IpmMetricTags, useIpmRenderTiming} from '@github-ui/ipm-telemetry'
import {Profiler, useEffect, useMemo} from 'react'
import {BannerNudgeCardPartial, type BannerNudgeCardPartialProps} from '../banner-nudge-card/BannerNudgeCardPartial'
import {Stackable} from './Stackable'

export interface NudgeCardStackPartialProps {
  cards: BannerNudgeCardPartialProps[]
}

// Thin partial wrapper that bridges the JSON-props world (Ruby
// `render_react_partial`) to the React-children world (`Stackable`). The
// underlying `Stackable` stays a generic stacker that takes any children; this
// wrapper just maps `cards` into `BannerNudgeCardPartial` elements.
export function NudgeCardStackPartial({cards}: NudgeCardStackPartialProps) {
  const hasCards = (cards?.length ?? 0) > 0
  const displayLocation = cards?.[0]?.analytics.location ?? 'unknown'
  const telemetryTags = useMemo<IpmMetricTags>(
    () => ({
      package: 'in-product-targeting',
      component: 'NudgeCardStack',
      surface: 'nudge_card',
      display_location: displayLocation,
      category: 'nudge_card_stack',
      nudge_id: 'nudge_card_stack',
      variant: 'stacked_cards',
    }),
    [displayLocation],
  )
  const renderTiming = useIpmRenderTiming(telemetryTags)

  useEffect(() => {
    if (hasCards) {
      reportIpmVisible(telemetryTags)
    }
  }, [hasCards, telemetryTags])

  if (!cards || cards.length === 0) {
    return null
  }

  const stack = (
    <Stackable telemetryTags={telemetryTags}>
      {cards.map(card => (
        <BannerNudgeCardPartial key={card.testSelector} {...card} />
      ))}
    </Stackable>
  )

  const content = !renderTiming.isEnabled ? (
    stack
  ) : (
    <Profiler id="NudgeCardStack" onRender={renderTiming.onRender}>
      {stack}
    </Profiler>
  )

  return (
    <IpmErrorBoundary tags={telemetryTags} boundaryName="NudgeCardStack">
      {content}
    </IpmErrorBoundary>
  )
}
